import { Label } from '@progress/kendo-react-labels';
import cloneDeep from 'lodash.clonedeep';
import { defaultMarkdownSerializer } from 'prosemirror-markdown';
import { useContext, useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { HtmlEditor } from '../../../components/editor/html-editor';
import { StandardInput, StandardTextarea } from '../../../components/forms';
import { MarkdownEditor } from '../../../components/markdown/markdown';
import { Questionnaire } from '../../../components/questionnaire/questionnaire';
import { GameDocumentContext } from '../../../contexts/game-document';
import { uuid } from '../../../types/common-helper';
import { GameDocument } from '../../../types/game-document';
import {
  ResourceEntity,
  TaskContentEntity
} from '../../../types/game-document/entities';
import {
  AnswerType,
  FormCustomFeedback,
  ManualScoring,
  ScoringReductionEntity,
  TARMiniGame,
  TaskContentAnswer,
  TaskContentForm
} from '../../../types/game-document/entities/task-content';
import RequiredFields from '../../../types/required-fields';
import {
  AddDefaultAnswers,
  EnsureNumberFormAnswers,
  AddResourceAsync,
  AddResourceEntityAsync,
  DeleteResourceAsync,
  UpdateGameDocState,
  UpdateResourceAsync,
  GetResourceValue
} from '../../../utils/game-document';
import {
  AddAnswerTaskContentForm,
  AddNewTaskContentForm,
  CopyTaskContentFormQuestion,
  DefaultCustomFeedback,
  DeleteAnswerTaskContentForm,
  DeleteTaskContentForm,
  UpdateTaskContentAsync,
  UpdateTaskContentFormAnswers
} from '../../../utils/game-document/assets';
import {
  ResourceWindow,
  UploadedImage
} from '../image-resource/resource-window';
import { ExpansionPanel } from '@progress/kendo-react-layout';
import { Reveal } from '@progress/kendo-react-animation';
import { Reorder, useDragControls } from 'motion/react';
import { ExpansionContentDisabledKeys } from '../../../components/expansion-content';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { GetDefaultMessage } from '../../../utils/game-document/feedback-content';
import { Checkbox } from '@progress/kendo-react-inputs';
import OnDemandTaskComboBox from '../../../components/combobox/on-demand-task-combobox';
import { ARGameSetting } from './AR-game-setting';
import { ARMiniGameContent } from '../../../constants/ar-mini-game';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ERR_INPUT_REQUIRED } from '../../../constants/text';

type FileUpload = 'Background' | 'Logo' | 'Media';
export interface TaskContentFullEditorProps {
  entity: TaskContentEntity;
  requiredFields?: RequiredFields<TaskContentEntity>[];
  handleEntityChange?: (entityKey: string, value: string) => void;
}

export interface TaskContentType {
  id?: AnswerType;
  description?: string;
}

export const TaskContentFullEditor = ({
  entity,
  requiredFields,
  handleEntityChange = () => {}
}: TaskContentFullEditorProps) => {
  const { taskContentId } = useParams();
  const { state: stateLocation, pathname } = useLocation();
  const navigate = useNavigate();

  const [state, setState] = useContext(GameDocumentContext);
  const [resources, setResources] = useState<ResourceEntity[]>([]);
  const [fileUpload, setFileUpload] = useState<FileUpload>();
  const [isEditMedia, setIsEditMedia] = useState<boolean>(false);
  const [taskContentForm, setTaskContentForm] = useState<TaskContentForm>();
  const [entityEditorIsVisible, setEntityEditorIsVisible] =
    useState<boolean>(false);
  const [editMediaResourceIndex, setEditMediaResourceIndex] = useState<
    number | undefined
  >(undefined);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');

  const [taskContent, setTaskContent] = useState<TaskContentEntity>(
    {} as TaskContentEntity
  );
  const { forms = [] } = taskContent;

  const onAddNewForm = () => {
    AddNewTaskContentForm(state.gameDocument!, taskContent.id).then(
      (response) => {
        setState((prev) => UpdateGameDocState(prev, response));
      }
    );
  };

  const copyForm = (index: number) => {
    CopyTaskContentFormQuestion(
      state.gameDocument!,
      taskContent.id,
      taskContent?.forms![index]?.questionResId!
    ).then((response) => {
      setState((prev) => UpdateGameDocState(prev, response));
    });
  };

  const onDeleteFormHandler = (index: string) => {
    const questionId = forms[parseInt(index)].questionResId;

    DeleteTaskContentForm(
      state.gameDocument!,
      taskContent?.id,
      questionId!
    ).then((response) => {
      let result = cloneDeep(response);
      const tasks = (result.assets.tasks ?? []).map((t) => {
        if (t.taskContentId !== taskContentId) return t;

        if (!t.events || !t.events.onCompleteTask) return t;
        const onComplete = (t.events.onCompleteTask.steps ?? []).map((s) => {
          if (s.operation === 'choosesAnswer') s.argumentAssetId = '';

          return s;
        });
        t.events.onCompleteTask.steps = onComplete;

        return t;
      });

      result.assets.tasks = tasks;
      setState((prev) => UpdateGameDocState(prev, result));
    });
  };

  const onAddAnswerHandler = (questionIndex: number) => {
    const questionId = forms[questionIndex].questionResId;

    AddAnswerTaskContentForm(
      state.gameDocument!,
      taskContent?.id,
      questionId!
    ).then((response) => {
      setState((prev) => UpdateGameDocState(prev, response!));
    });
  };

  const onDeleteOptionHandler = (index: number, optionIndex: number) => {
    const questionResId = forms[index].questionResId;
    const answerResId = forms![index!].answers![optionIndex].answerResId;
    DeleteAnswerTaskContentForm(
      state.gameDocument!,
      taskContent?.id,
      questionResId!,
      answerResId
    ).then((response) => {
      let result = cloneDeep(response);
      const tasks = (result.assets.tasks ?? []).map((t) => {
        if (t.taskContentId !== taskContentId) return t;

        if (!t.events || !t.events.onCompleteTask) return t;
        const onComplete = (t.events.onCompleteTask.steps ?? []).map((s) => {
          if (s.operation === 'choosesAnswer')
            s.argumentAssetId = (s.argumentAssetId as string[]).filter(
              (a) => a !== answerResId
            );

          return s;
        });
        t.events.onCompleteTask.steps = onComplete;

        return t;
      });

      result.assets.tasks = tasks;
      setState((prev) => UpdateGameDocState(prev, result));
    });
  };

  const getAnswersDescription = (form: TaskContentForm) => {
    let answerDescriptions: TaskContentAnswer[] = [];

    form.answers?.forEach((answer: TaskContentAnswer) => {
      answer.description = resources.find(
        (x) => x?.id === answer.answerResId
      )?.value;

      answerDescriptions.push(answer);
    });

    return answerDescriptions;
  };

  const onChangeScoringHandler = (
    formId: string,
    value: number | undefined,
    isManualScoring?: boolean,
    manualScoring?: ManualScoring,
    maxAttempts?: number | undefined,
    pointDepreciation?: number | undefined,
    isScoringReduction?: boolean,
    scoringReduction?: ScoringReductionEntity,
    answersWithScoring?: TaskContentAnswer[]
  ) => {
    let newTaskContent: TaskContentEntity = { ...taskContent };

    const index = newTaskContent?.forms?.findIndex((x) => x.id === formId);

    if (index! > -1) {
      newTaskContent.forms![index!].score = value;
      newTaskContent.forms![index!].isManualScoring = isManualScoring;
      newTaskContent.forms![index!].manualScoring = manualScoring;
      newTaskContent.forms![index!].maxAttempts = maxAttempts;
      newTaskContent.forms![index!].pointDepreciation = pointDepreciation;
      newTaskContent.forms![index!].isScoringReduction = isScoringReduction;
      newTaskContent.forms![index!].scoringReduction = scoringReduction;
      newTaskContent.forms![index!].answers = answersWithScoring;

      UpdateTaskContentAsync(
        state.gameDocument!,
        newTaskContent.id,
        newTaskContent
      ).then((response) => {
        setState((prev) => UpdateGameDocState(prev, response!));
      });
    }
  };

  /**
   * Update custom feedback to task content form
   * @param formId
   * @param customFeedback
   */
  const onCustomFeedbackHandler = async (
    formId: string,
    customFeedback: FormCustomFeedback,
    uploadedImage: {
      allCorrectUrlResId: UploadedImage | null;
      allWrongUrlResId: UploadedImage | null;
      partiallyCorrectUrlResId: UploadedImage | null;
    }
  ) => {
    if (state.gameDocument) {
      let updatedGameDocument = cloneDeep(state.gameDocument);
      const newTaskContent: TaskContentEntity = { ...taskContent };
      const index = newTaskContent?.forms?.findIndex((x) => x?.id === formId);

      if (index! > -1) {
        const addIconResource = async (
          uploadedImage: UploadedImage,
          resId:
            | 'allCorrectUrlResId'
            | 'partiallyCorrectUrlResId'
            | 'allWrongUrlResId'
        ) => {
          const resourceId = uuid();
          const resourceType = uploadedImage.mimeType?.split('/')[0];
          updatedGameDocument = await AddResourceAsync(
            state.gameDocument!,
            uploadedImage.name || uploadedImage.fileName || '',
            uploadedImage.fileName ?? '',
            resourceType,
            uploadedImage.blobUrl,
            resourceId,
            uploadedImage.size,
            uploadedImage.category
          );

          customFeedback[resId] = resourceId;
        };

        if (uploadedImage.allCorrectUrlResId)
          await addIconResource(
            uploadedImage.allCorrectUrlResId,
            'allCorrectUrlResId'
          );
        if (uploadedImage.allWrongUrlResId)
          await addIconResource(
            uploadedImage.allWrongUrlResId,
            'allWrongUrlResId'
          );
        if (uploadedImage.partiallyCorrectUrlResId)
          await addIconResource(
            uploadedImage.partiallyCorrectUrlResId,
            'partiallyCorrectUrlResId'
          );

        newTaskContent.forms![index!].customFeedback = customFeedback;
        updatedGameDocument = await UpdateTaskContentAsync(
          state.gameDocument!,
          newTaskContent.id,
          newTaskContent
        );

        setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
      }
    }
  };

  /**
   * Update custom feedback to task content form
   * @param formId
   * @param customFeedback
   */
  const onDeleteMediaHandler = (formId: string, mediaIndex: number) => {
    let newTaskContent: TaskContentEntity = { ...taskContent };

    const index = newTaskContent?.forms?.findIndex((x) => x.id === formId);

    if (index! > -1) {
      newTaskContent.forms![index!].medias?.splice(mediaIndex, 1);

      UpdateTaskContentAsync(
        state.gameDocument!,
        newTaskContent.id,
        newTaskContent
      ).then((response) => {
        setState((prev) => UpdateGameDocState(prev, response!));
      });
    }
  };

  const onChangeAnswersHandler = (
    formId: string,
    answers: TaskContentAnswer[]
  ) => {
    UpdateTaskContentFormAnswers(
      state.gameDocument!,
      taskContent.id,
      formId,
      answers
    ).then((response) => {
      setState((prev) => UpdateGameDocState(prev, response!));
    });
  };

  const onChangeAnswerTypeHandler = async (index: number, type: AnswerType) => {
    let newTaskContent = { ...taskContent } as TaskContentEntity;
    let updatedGameDocument = state.gameDocument;

    if (newTaskContent.forms && updatedGameDocument) {
      if (
        newTaskContent.forms![index].answers &&
        newTaskContent.forms![index].answers.length > 0
      ) {
        const removeOldAnswerResources =
          newTaskContent.forms![index].answers?.map((a) => a.answerResId) ?? [];

        // Delete old answer resources
        await Promise.all(
          removeOldAnswerResources.map((resId) =>
            DeleteResourceAsync(updatedGameDocument!, resId)
          )
        ).then((responses) => {
          updatedGameDocument = responses[responses.length - 1];
        });
      }

      newTaskContent!.forms![index].type = type;

      // Reset the answer every change answer type
      newTaskContent.forms![index].answers = [];

      const customFeedback = newTaskContent.forms[index].customFeedback;

      // check if feedback is enable
      if (customFeedback) {
        if (type === 'checkbox') {
          // should add feedback partial message if answer type is checkbox
          const partialMessageFeedback = {
            isDefaultPartiallyCorrect: true,
            partiallyCorrectUrlResId: '',
            partiallyCorrectMessageResId: uuid()
          };

          AddResourceAsync(
            updatedGameDocument,
            'Partial correct feedback message default',
            '',
            'text',
            'That answer was partially correct! Partial points awarded.',
            partialMessageFeedback.partiallyCorrectMessageResId
          ).then((response) => (updatedGameDocument = response));

          newTaskContent.forms[index].customFeedback = {
            ...customFeedback,
            ...partialMessageFeedback
          };
        } else if (['textarea', 'none'].includes(type)) {
          // should remove feedback if answer type paraghraph (textarea) or task manual (none)
          const {
            allCorrectMessageResId,
            allWrongMessageResId,
            partiallyCorrectMessageResId
          } = customFeedback;
          if (allCorrectMessageResId)
            DeleteResourceAsync(
              updatedGameDocument,
              allCorrectMessageResId
            ).then((response) => (updatedGameDocument = response));
          if (allWrongMessageResId)
            DeleteResourceAsync(updatedGameDocument, allWrongMessageResId).then(
              (response) => (updatedGameDocument = response)
            );
          if (partiallyCorrectMessageResId)
            DeleteResourceAsync(
              updatedGameDocument,
              partiallyCorrectMessageResId
            ).then((response) => (updatedGameDocument = response));

          delete newTaskContent.forms[index].customFeedback;
        } else {
          const { partiallyCorrectMessageResId } = customFeedback;
          if (partiallyCorrectMessageResId)
            DeleteResourceAsync(
              updatedGameDocument,
              partiallyCorrectMessageResId
            ).then((response) => (updatedGameDocument = response));

          newTaskContent.forms[index].customFeedback = {
            ...customFeedback,

            isDefaultPartiallyCorrect: true,
            partiallyCorrectUrlResId: '',
            partiallyCorrectMessageResId: ''
          };
        }
      } else {
        // feedback default should be enable, so if there is no feedback
        // and answer type is not paraghraph (textarea) or task manual (none)
        // it should be added
        if (!['textarea', 'none'].includes(type)) {
          const correctMessageFeedback = {
            isDefaultAllCorrect: true,
            allCorrectUrlResId: '',
            allCorrectMessageResId: uuid()
          };
          const wrongMessageFeedback = {
            isDefaultAllWrong: true,
            allWrongUrlResId: '',
            allWrongMessageResId: uuid()
          };
          const partialMessageFeedback = {
            isDefaultPartiallyCorrect: true,
            partiallyCorrectUrlResId: '',
            partiallyCorrectMessageResId: uuid()
          };

          const defaultFeedback = {
            ...DefaultCustomFeedback(),
            ...correctMessageFeedback,
            ...wrongMessageFeedback,
            ...(type === 'checkbox' && { ...partialMessageFeedback })
          };

          AddResourceAsync(
            updatedGameDocument,
            'All correct feedback message default',
            '',
            'text',
            GetDefaultMessage(
              'defaultAllCorrectFeedbackMessageResId',
              updatedGameDocument
            ),
            correctMessageFeedback.allCorrectMessageResId
          ).then((response) => (updatedGameDocument = response));
          AddResourceAsync(
            updatedGameDocument,
            'All wrong feedback message default',
            '',
            'text',
            GetDefaultMessage(
              'defaultAllWrongFeedbackMessageResId',
              updatedGameDocument
            ),
            wrongMessageFeedback.allWrongMessageResId
          ).then((response) => (updatedGameDocument = response));
          if (type === 'checkbox') {
            AddResourceAsync(
              updatedGameDocument,
              'Partial correct feedback message default',
              '',
              'text',
              'That answer was partially correct! Partial points awarded.',
              partialMessageFeedback.partiallyCorrectMessageResId
            ).then((response) => (updatedGameDocument = response));
          }

          newTaskContent.forms[index].customFeedback = defaultFeedback;
        }
      }

      let newAnswerResources: ResourceEntity[] = AddDefaultAnswers(type);

      // #region Add answer resource
      AddMultipleAnswerForOptions(updatedGameDocument, newAnswerResources);

      newAnswerResources.forEach((responseAnswer) => {
        newTaskContent.forms![index].answers?.push({
          id: uuid(),
          answerResId: responseAnswer.id
        });
      });

      UpdateTaskContentAsync(
        updatedGameDocument,
        newTaskContent.id,
        newTaskContent
      ).then((response) => {
        setState((prev) => UpdateGameDocState(prev, response!));
      });
    }
  };

  const AddMultipleAnswerForOptions = async (
    gameDocument: GameDocument,
    resources: ResourceEntity[]
  ) => {
    resources.forEach((resource) => {
      AddResourceEntityAsync(gameDocument, resource).then((response) => {
        setState((prev) => UpdateGameDocState(prev, response!));
      });
    });
  };

  const onCopyFormHandler = (id: string) => {
    copyForm(parseInt(id));
  };

  const onChangeHandler = (
    value: string,
    type:
      | 'entity'
      | 'resource'
      | 'question'
      | 'answer'
      | 'media-only-identifier' = 'entity',
    keyId: string,
    answerFormId?: string
  ) => {
    if (type === 'entity') {
      handleEntityChange(keyId, value);
    } else {
      let resource: ResourceEntity | undefined;
      if (type === 'resource') {
        const keyResId = taskContent[keyId as keyof TaskContentEntity];
        setResources((prev) =>
          prev.map((item: ResourceEntity) =>
            item?.id === keyResId ? { ...item, value } : { ...item }
          )
        );
        resource = resources.find(
          (item: ResourceEntity) => item?.id === keyResId
        );
      } else if (type === 'question') {
        const form = taskContent.forms?.find(
          (item: TaskContentForm) => item.id === keyId
        );
        if (form) {
          const { questionResId } = form;
          setResources((prev) =>
            prev.map((item: ResourceEntity) =>
              item?.id === questionResId ? { ...item, value } : { ...item }
            )
          );
          resource = resources.find(
            (item: ResourceEntity) => item.id === questionResId
          );
        }
      } else if (type === 'answer') {
        setResources((prev) =>
          prev.map((item: ResourceEntity) =>
            item?.id === keyId ? { ...item, value } : { ...item }
          )
        );
        resource = resources.find((item: ResourceEntity) => item.id === keyId);

        // Currently, we saved answer inside form description, so we need this code for consistency only
        // Later need to remove this description and get/set answer directly to resources
        const form = taskContent.forms?.find(
          (item: TaskContentForm) => item?.id === answerFormId
        );
        if (form) {
          const answer = form.answers?.find(
            (item: TaskContentAnswer) => item.answerResId === keyId
          );
          if (answer) {
            answer.description = value;
            UpdateTaskContentAsync(
              state.gameDocument!,
              taskContent.id,
              taskContent
            ).then((response) =>
              setState((prev) => UpdateGameDocState(prev, response))
            );
          }
        }
      } else if (type === 'media-only-identifier') {
        const form = taskContent.forms?.find(
          (item: TaskContentForm) => item.id === keyId
        );
        if (form) {
          const { mediaOnlyIdentifierResId } = form;
          setResources((prev) =>
            prev.map((item: ResourceEntity) =>
              item?.id === mediaOnlyIdentifierResId
                ? { ...item, value }
                : { ...item }
            )
          );
          resource = resources.find(
            (item: ResourceEntity) => item.id === mediaOnlyIdentifierResId
          );
        }
      }

      if (resource) {
        resource.value = value;
        UpdateResourceAsync(state.gameDocument!, resource.id, resource).then(
          (response) => setState((prev) => UpdateGameDocState(prev, response))
        );
      }
    }
  };

  const onAddMedia = (form: TaskContentForm) => {
    setFileUpload('Media');
    setSelectedImageUrl('');
    setIsEditMedia(false);
    setEditMediaResourceIndex(undefined);
    setEntityEditorIsVisible(true);
    setTaskContentForm(form);
  };

  const toggleEntityEditor = () =>
    setEntityEditorIsVisible(!entityEditorIsVisible);

  const onMediaEditHandler = (form: TaskContentForm, mediaIndex: number) => {
    const resId = form.medias?.at(mediaIndex);
    const mediaUrl =
      resId && state.gameDocument
        ? GetResourceValue(state.gameDocument, resId)
        : '';
    setSelectedImageUrl(mediaUrl);
    setTaskContentForm(form);
    setEditMediaResourceIndex(mediaIndex);
    setIsEditMedia(true);
    toggleEntityEditor();
  };

  const handleEntityWindowEditorSubmit = async (
    uploadedImage: UploadedImage,
    target: FileUpload
  ) => {
    //append into resources
    const resourceId = uuid();
    if (target === 'Media') {
      let newTaskContent: TaskContentEntity = { ...taskContent };
      const index = newTaskContent?.forms?.findIndex(
        (x) => x.id === taskContentForm?.id
      );

      if (index === undefined || index === -1) {
        if (entityEditorIsVisible) {
          setEntityEditorIsVisible(false);
        }
        setIsEditMedia(false);
        setEditMediaResourceIndex(undefined);
        setTaskContentForm(undefined);
        setSelectedImageUrl('');
        return;
      }

      const resourceType = uploadedImage?.mimeType?.split('/')[0];
      let updatedGameDocument = state.gameDocument!;

      if (!uploadedImage.resource) {
        updatedGameDocument = await AddResourceAsync(
          updatedGameDocument,
          uploadedImage.name || uploadedImage.fileName || '',
          uploadedImage.fileName ?? '',
          resourceType,
          uploadedImage.blobUrl,
          resourceId,
          uploadedImage.size,
          uploadedImage.category
        );
      } else {
        const resourceEntity = updatedGameDocument.resources.find(
          (res) => res.id === uploadedImage.resource!.id
        );
        if (resourceEntity) {
          const updatedResource = {
            ...resourceEntity,
            name:
              uploadedImage.name ||
              uploadedImage.fileName ||
              resourceEntity.name,
            category: uploadedImage.category ?? resourceEntity.category
          };
          updatedGameDocument = await UpdateResourceAsync(
            updatedGameDocument,
            updatedResource.id,
            updatedResource
          );
        }
      }

      if (!isEditMedia) {
        if (!newTaskContent.forms![index!].medias) {
          newTaskContent.forms![index!].medias = [];
        }

        newTaskContent.forms![index!].medias?.push(
          uploadedImage.resource ? uploadedImage.resource.id! : resourceId
        );
      } else {
        if (
          editMediaResourceIndex !== undefined &&
          newTaskContent!.forms &&
          newTaskContent!.forms[index!]?.medias
        ) {
          newTaskContent!.forms[index!].medias![editMediaResourceIndex] =
            uploadedImage.resource ? uploadedImage.resource.id! : resourceId;
        }
      }

      updatedGameDocument = await UpdateTaskContentAsync(
        updatedGameDocument,
        entity.id,
        newTaskContent
      );

      setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
      setIsEditMedia(false);
      setEditMediaResourceIndex(undefined);
      setTaskContentForm(undefined);
      setSelectedImageUrl('');
    }

    if (entityEditorIsVisible) {
      setEntityEditorIsVisible(false);
    }
  };

  const migrationDoneRef = useRef<string | null>(null);

  useEffect(() => {
    if (entity) {
      // Safety net: migrate legacy number-type forms with only 1 answer to 2 answers (min/max)
      // Use ref guard to prevent duplicate concurrent migration calls
      if (state.gameDocument && migrationDoneRef.current !== entity.id) {
        migrationDoneRef.current = entity.id;
        EnsureNumberFormAnswers(state.gameDocument, entity.id).then(
          ({ gameDocument: updatedDoc, migrated }) => {
            if (migrated) {
              setState((prev) => UpdateGameDocState(prev, updatedDoc));
              return;
            }
          }
        );
      }

      setTaskContent(cloneDeep({ ...entity }));
      const titleRes: ResourceEntity = state.gameDocument?.resources.find(
        (item) => item.id === entity.titleResId
      )!;
      const contentRes: ResourceEntity = state.gameDocument?.resources.find(
        (item) => item.id === entity.contentResId
      )!;
      const preMessageRes: ResourceEntity = state.gameDocument?.resources.find(
        (item) => item.id === entity.preMessageResId
      )!;
      const taskContentForm: TaskContentForm[] =
        state.gameDocument?.assets.taskContents?.find(
          (item) => item.id === entity.id
        )?.forms ?? [];
      const questionsRes: ResourceEntity[] = [];
      const mediaOnlyIdentifiersRes: ResourceEntity[] = [];
      const answersRes: ResourceEntity[] = [];
      taskContentForm.forEach((item: TaskContentForm) => {
        const questionRes: ResourceEntity | undefined =
          state.gameDocument?.resources.find(
            (resource) => resource.id === item.questionResId
          );
        const mediaOnlyIdentifierRes: ResourceEntity | undefined =
          state.gameDocument?.resources.find(
            (resource) => resource.id === item.mediaOnlyIdentifierResId
          );
        if (questionRes) questionsRes.push(questionRes);
        if (mediaOnlyIdentifierRes)
          mediaOnlyIdentifiersRes.push(mediaOnlyIdentifierRes);
        item.answers?.forEach((answer: TaskContentAnswer) => {
          const answerRes: ResourceEntity | undefined =
            state.gameDocument?.resources.find(
              (resource) => resource.id === answer.answerResId
            );
          if (answerRes) answersRes.push(answerRes);
        });
      });
      setResources(
        [titleRes, contentRes, preMessageRes]
          .concat(questionsRes)
          .concat(mediaOnlyIdentifiersRes)
          .concat(answersRes)
      );
    }
  }, [state, entity]);

  const tabs =
    entity?.ARMiniGame !== undefined
      ? ['Information', 'Premessage', 'Intro', 'AR Game Setting']
      : ['Information', 'Premessage', 'Intro', 'Forms'];
  const [activeTab, setActiveTab] = useState('Information');
  const [isExpand, setIsExpand] = useState({
    info: true,
    premessage: true,
    intro: true,
    forms: true,
    arSetting: true
  });

  const toggleExpand = (tab: keyof typeof isExpand) =>
    setIsExpand((prev) => ({
      ...prev,
      [tab]: !prev[tab]
    }));

  const dragControls = useDragControls();

  const onChangeARMiniGame = (type: TARMiniGame) => {
    onChangeHandler(type, 'resource', 'titleResId');
    const selectedAR = ARMiniGameContent[type];
    onChangeHandler(selectedAR.premessage, 'resource', 'preMessageResId');
    onChangeHandler(selectedAR.intro, 'resource', 'contentResId');
  };

  const onRemoveODTCategory = (index: number) => {
    const categories = cloneDeep(
      state.gameDocument?.assets.onDemandTaskCategories ?? []
    );
    const selectedCategory = categories[index];
    const taskContents = cloneDeep(
      state.gameDocument?.assets.taskContents ?? []
    ).map((taskContent) => {
      if (
        taskContent.onDemandTaskCategory &&
        taskContent.onDemandTaskCategory === selectedCategory
      ) {
        taskContent.onDemandTaskCategory = '';
      }

      return taskContent;
    });

    categories.splice(index, 1);
    setState((prev) =>
      UpdateGameDocState(prev, {
        ...prev.gameDocument!,
        assets: {
          ...prev.gameDocument?.assets,
          taskContents,
          onDemandTaskCategories: categories
        }
      })
    );
  };

  const InformationTab = (
    <div className={'d-flex flex-column gap-2'}>
      <Row>
        <Col sm={'12'} md={'12'} lg={'8'} xl={'8'}>
          <StandardInput
            name={'titleResId'}
            id={`title-field-${taskContentId}`}
            label={'Task content title'}
            labelStyle={'fw-normal'}
            value={
              resources.find((item) => item.id === taskContent.titleResId)
                ?.value ?? ''
            }
            validationMessage={
              !resources.find((item) => item.id === taskContent.titleResId)
                ?.value
                ? ERR_INPUT_REQUIRED
                : ''
            }
            onChange={(e) => {
              onChangeHandler(
                e.target.value as string,
                'resource',
                'titleResId'
              );
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={'12'} md={'12'} lg={'8'} xl={'8'}>
          <StandardTextarea
            name={'description'}
            label={'Task content description'}
            labelStyle={'fw-normal'}
            rows={2}
            autoSize
            value={entity?.description}
            onChange={(e) => {
              onChangeHandler(
                e.target.value as string,
                'entity',
                e.target.name!
              );
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={'12'} md={'12'} lg={'8'} xl={'8'}>
          <Label className="fw-normal">Body type</Label>
          <DropDownList
            value={entity?.bodyType}
            style={{
              backgroundColor: 'white'
            }}
            onChange={(e) =>
              onChangeHandler(e.target.value as string, 'entity', 'bodyType')
            }
            data={['HTML', 'Markdown']}
          />
        </Col>
      </Row>

      <Row>
        <Col sm={'12'} md={'12'} lg={'8'} xl={'8'}>
          <Checkbox
            label="AR mini game"
            checked={entity?.ARMiniGame !== undefined}
            onChange={(e) => {
              onChangeHandler(
                e.value ? 'Where in the World? - Hard' : '',
                'entity',
                'ARMiniGame'
              );
              if (e.value) onChangeARMiniGame('Where in the World? - Hard');
            }}
          />

          {entity?.ARMiniGame !== undefined && (
            <DropDownList
              style={{
                backgroundColor: 'white'
              }}
              data={[
                'Where in the World? - Easy',
                'Where in the World? - Hard',
                'Summer Suitcase',
                'Sound Waves',
                'The Portal',
                'Meerkats Moves',
                'Game Cube'
              ]}
              value={
                entity?.ARMiniGame === ('Where in the World?' as any)
                  ? 'Where in the World? - Hard'
                  : entity?.ARMiniGame
              }
              onChange={(e) => {
                onChangeHandler(e.value, 'entity', 'ARMiniGame');
                onChangeARMiniGame(e.value);
              }}
            />
          )}
        </Col>
      </Row>

      <Row>
        <Col sm={'12'} md={'12'} lg={'8'} xl={'8'}>
          <Checkbox
            label={'On-demand task'}
            checked={entity?.onDemandTaskCategory !== undefined}
            onChange={(e) => {
              if (e.value) {
                onChangeHandler('', 'entity', 'onDemandTaskCategory');
              } else {
                const clone = cloneDeep(entity);
                delete clone.onDemandTaskCategory;
                UpdateTaskContentAsync(
                  state.gameDocument!,
                  entity?.id,
                  clone,
                  false
                ).then((response) => {
                  setState((prev) => UpdateGameDocState(prev, response));
                });
              }
            }}
          />

          {entity?.onDemandTaskCategory !== undefined && (
            <>
              <Label className={'fw-normal'}>On-demand task category</Label>
              <OnDemandTaskComboBox
                style={{
                  width: '100%'
                }}
                onRemoveODTCategory={onRemoveODTCategory}
                category={
                  state.gameDocument?.assets.onDemandTaskCategories ?? []
                }
                value={entity?.onDemandTaskCategory}
                onChange={(e) =>
                  onChangeHandler(
                    e.value as string,
                    'entity',
                    'onDemandTaskCategory'
                  )
                }
              />
            </>
          )}
        </Col>
      </Row>
    </div>
  );

  const PremessageTab = (
    <Row>
      <Col sm={'12'} md={'12'} lg={'8'} xl={'8'}>
        {taskContent.bodyType === 'HTML' && (
          <HtmlEditor
            value={
              resources.find((item) => {
                const resId = taskContent.preMessageResId;
                return item?.id === resId;
              })?.value ?? ''
            }
            handleHtmlChange={(e) => {
              onChangeHandler(e.html, 'resource', 'preMessageResId');
            }}
            resourceCategory={'Task Content Media'}
          />
        )}

        {taskContent.bodyType === 'Markdown' && (
          <MarkdownEditor
            key={entity?.id}
            value={
              resources.find((item) => item.id === taskContent.preMessageResId)
                ?.value ?? ''
            }
            handleMarkdownChange={(e) => {
              onChangeHandler(
                // @ts-ignore expect mismatched types.
                defaultMarkdownSerializer.serialize(e.value),
                'resource',
                'preMessageResId'
              );
            }}
          />
        )}
      </Col>
    </Row>
  );

  const IntroTab = (
    <Row>
      <Col sm={'12'} md={'12'} lg={'8'} xl={'8'}>
        {taskContent.bodyType === 'HTML' && (
          <HtmlEditor
            value={
              resources.find((item) => item?.id === taskContent?.contentResId)
                ?.value ?? ''
            }
            handleHtmlChange={(e) => {
              onChangeHandler(e.html, 'resource', 'contentResId');
            }}
            resourceCategory={'Task Content Media'}
          />
        )}

        {taskContent.bodyType === 'Markdown' && (
          <MarkdownEditor
            value={
              resources.find((item) => item.id === taskContent.contentResId)
                ?.value ?? ''
            }
            handleMarkdownChange={(e) => {
              onChangeHandler(
                // @ts-ignore expect mismatched types.
                defaultMarkdownSerializer.serialize(e.value),
                'resource',
                'contentResId'
              );
            }}
          />
        )}
      </Col>
    </Row>
  );

  const FormsTab = (
    <>
      <Reorder.Group
        axis={'y'}
        className={'p-0'}
        values={forms}
        onReorder={(forms) => {
          UpdateTaskContentAsync(state.gameDocument!, taskContent.id, {
            ...taskContent,
            forms
          }).then((response) =>
            setState((prev) => UpdateGameDocState(prev, response))
          );
        }}>
        {forms.map((form: TaskContentForm, index: number) => {
          return (
            <Reorder.Item
              value={form}
              id={form.id}
              className={'d-flex gap-2 align-items-center'}
              style={{
                listStyle: 'none'
              }}
              key={form.id}>
              <div className={'card p-3 mt-3 flex-grow-1'}>
                <Questionnaire
                  key={form.id}
                  id={index.toString()}
                  score={form.score}
                  answerType={form.type}
                  isManualScoring={form?.isManualScoring}
                  manualScoring={form?.manualScoring}
                  isScoringReduction={form?.isScoringReduction}
                  scoringReduction={form?.scoringReduction}
                  maxAttempts={form?.maxAttempts}
                  pointDepreciation={form?.pointDepreciation}
                  answers={getAnswersDescription(form)}
                  customFeedback={form?.customFeedback}
                  taskContentId={taskContent.id}
                  onChangeAnswerType={onChangeAnswerTypeHandler}
                  onChangeAnswers={(answers) =>
                    onChangeAnswersHandler(form.id, answers)
                  }
                  question={
                    resources.find((x) => x?.id === form.questionResId)
                      ?.value ?? ''
                  }
                  mediaOnlyIdentifier={
                    resources.find(
                      (x) => x.id === form.mediaOnlyIdentifierResId
                    )?.value ?? ''
                  }
                  onAddAnswer={onAddAnswerHandler}
                  onDeleteOption={onDeleteOptionHandler}
                  onCopyForm={onCopyFormHandler}
                  onDeleteForm={onDeleteFormHandler}
                  onChangeScoring={(
                    score,
                    isManualScoring,
                    manualScoring,
                    maxAttempts,
                    pointDepreciation,
                    isScoringReduction,
                    scoringReduction,
                    answersWithScoring
                  ) =>
                    onChangeScoringHandler(
                      form.id,
                      score,
                      isManualScoring,
                      manualScoring,
                      maxAttempts,
                      pointDepreciation,
                      isScoringReduction,
                      scoringReduction,
                      answersWithScoring
                    )
                  }
                  handleInputChange={(
                    value: string,
                    type: 'question' | 'answer' | 'media-only-identifier',
                    answerResId?: string
                  ) => {
                    if (['question', 'media-only-identifier'].includes(type)) {
                      onChangeHandler(value, type, form.id);
                    } else if (type === 'answer') {
                      onChangeHandler(value, type, answerResId!, form.id);
                    } else {
                      onChangeHandler(value, type, form.id);
                    }
                  }}
                  onCustomFeedbackChange={(val, uploadedImage) => {
                    onCustomFeedbackHandler(form.id, val, uploadedImage);
                  }}
                  onAddMedia={() => onAddMedia(form)}
                  taskContentForm={form}
                  onDeleteMedia={(mediaIndex) =>
                    onDeleteMediaHandler(form.id, mediaIndex)
                  }
                  onEditMedia={(mediaIndex) =>
                    onMediaEditHandler(form, mediaIndex)
                  }
                />
              </div>

              <div
                onPointerDown={(e) => dragControls.start(e)}
                className={
                  'text-center d-flex align-items-center justify-content-center'
                }
                style={{
                  width: '5%',
                  cursor: 'grab',
                  height: '100%'
                }}>
                <span className={'material-symbols-outlined'}>drag_handle</span>
              </div>
            </Reorder.Item>
          );
        })}
      </Reorder.Group>

      <Row>
        <Col>
          <div
            className={
              'card cursor-pointer p-3 mt-3 bg-base-bg text-decoration-none'
            }
            onClick={onAddNewForm}>
            <div className={'card-body d-flex justify-content-center'}>
              <span className={'material-symbols-outlined'}>dns</span>
              <Label className={'cursor-pointer text-decoration-none'}>
                {forms.length === 0 ? 'Add form' : 'Add question'}
              </Label>
            </div>
          </div>
        </Col>
      </Row>
    </>
  );

  useEffect(() => {
    if (!state.gameDocument || !stateLocation) return;

    const targetElement = document.getElementById(
      `${stateLocation?.field ?? 'name'}-field-${taskContentId}`
    );
    if (targetElement) {
      targetElement.classList.add('blip');
      setTimeout(() => {
        targetElement.classList.remove('blip');
      }, 1000);
      targetElement.scrollIntoView({
        behavior: 'smooth'
      });
      navigate(pathname, { replace: true });
    }
  }, [taskContentId, state.gameDocument, taskContentForm?.id, stateLocation]);

  return (
    <>
      <div className="p-0 m-0">
        <div className={'d-none d-md-flex flex-column'}>
          <ExpansionPanel
            title={'Information'}
            className={'mt-3'}
            expanded={isExpand.info}
            onAction={() => toggleExpand('info')}>
            <Reveal>
              {isExpand.info && (
                <ExpansionContentDisabledKeys>
                  {InformationTab}
                </ExpansionContentDisabledKeys>
              )}
            </Reveal>
          </ExpansionPanel>

          <ExpansionPanel
            title={'Premessage'}
            expanded={isExpand.premessage}
            onAction={() => toggleExpand('premessage')}>
            <Reveal>
              {isExpand.premessage && (
                <ExpansionContentDisabledKeys>
                  {PremessageTab}
                </ExpansionContentDisabledKeys>
              )}
            </Reveal>
          </ExpansionPanel>

          <ExpansionPanel
            title={'Intro'}
            expanded={isExpand.intro}
            onAction={() => toggleExpand('intro')}>
            <Reveal>
              {isExpand.intro && (
                <ExpansionContentDisabledKeys>
                  {IntroTab}
                </ExpansionContentDisabledKeys>
              )}
            </Reveal>
          </ExpansionPanel>

          {entity?.ARMiniGame !== undefined ? (
            <ExpansionPanel
              title={'AR Game Setting'}
              expanded={isExpand.arSetting}
              onAction={() => toggleExpand('arSetting')}>
              <Reveal>
                {isExpand.arSetting && (
                  <ExpansionContentDisabledKeys>
                    <ARGameSetting />
                  </ExpansionContentDisabledKeys>
                )}
              </Reveal>
            </ExpansionPanel>
          ) : (
            <ExpansionPanel
              title={'Forms'}
              expanded={isExpand.forms}
              onAction={() => toggleExpand('forms')}>
              <Reveal>
                {isExpand.forms && (
                  <ExpansionContentDisabledKeys>
                    {FormsTab}
                  </ExpansionContentDisabledKeys>
                )}
              </Reveal>
            </ExpansionPanel>
          )}
        </div>

        <div className={'d-flex flex-column mt-4 d-md-none gap-4'}>
          <div
            className={
              'd-flex justify-content-between align-items-center w-100 py-2 task-content-header'
            }>
            <h2 className={'m-0'}>
              {GetResourceValue(state.gameDocument!, entity.titleResId ?? '')}
            </h2>
            <div>
              <DropDownList
                onChange={(e) => setActiveTab(e.value)}
                style={{ width: '200px' }}
                data={tabs}
                defaultValue={'Information'}
              />
            </div>
          </div>

          {(() => {
            switch (activeTab) {
              case 'Information':
                return InformationTab;
              case 'Premessage':
                return PremessageTab;
              case 'Intro':
                return IntroTab;
              case 'Forms':
                return FormsTab;
              case 'AR Game Setting':
                return <ARGameSetting />;
            }
          })()}
        </div>
      </div>

      {entityEditorIsVisible ? (
        <ResourceWindow
          title={'Upload Form Media'}
          toggleDialog={() => {
            setTaskContentForm(undefined);
            setEditMediaResourceIndex(undefined);
            setIsEditMedia(false);
            setSelectedImageUrl('');
            toggleEntityEditor();
          }}
          onSubmit={(uploadedFile) =>
            handleEntityWindowEditorSubmit(uploadedFile, 'Media')
          }
          fileType={['audio', 'image', 'video']}
          imageUrl={selectedImageUrl}
          {...(taskContentForm &&
            editMediaResourceIndex !== undefined && {
              resourceId: taskContentForm.medias?.at(editMediaResourceIndex)
            })}
          defaultSelectedCategory={'Task Content Media'}
        />
      ) : null}
    </>
  );
};
